import { type ReactNode, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type MyShipmentResponse, type MyShipmentScope } from '@aps/contracts';
import {
  Button,
  EmptyState,
  FilterChip,
  Skeleton,
  formatMoney,
  formatSumInput,
  parseSumInput,
  useToast,
} from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';

/** Yo'ldagi ro'yxat shu oraliqda yangilanadi: dispetcher yangi topshirganini kuryer o'zi ko'radi. */
const REFRESH_MS = 30_000;

type Action = { kind: 'delivered' | 'failed' | 'cash'; id: string } | null;

/**
 * Kuryer ekrani: "Mening yetkazmalarim".
 *
 * Kuryer FAQAT o'ziga biriktirilgan jo'natmalarni ko'radi (server filtrlaydi, klient
 * hech narsani yashirmaydi). Har kartochkada kuryerga kerak narsa: mijoz va telefon,
 * manzil, kelishuv, tovarlar, to'lov holati. Natija ikki tugma: "Yetkazildi" (tasdiq
 * bilan — tasodifiy bosish qaytarilmas) va "Yetkazilmadi" (sabab MAJBURIY).
 */
export function MyDeliveriesScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const reasonId = useId();
  const cashId = useId();
  const [scope, setScope] = useState<MyShipmentScope>('active');
  const [action, setAction] = useState<Action>(null);
  const [reason, setReason] = useState('');
  const [cash, setCash] = useState('');

  const list = useQuery({
    queryKey: ['my-shipments', scope],
    queryFn: () => api.myShipments(scope),
    refetchInterval: REFRESH_MS,
  });

  const finish = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'delivered' | 'failed' }) =>
      api.courierResult(id, {
        toCode: kind === 'delivered' ? 'DELIVERED' : 'FAILED_DELIVERY',
        ...(kind === 'failed' ? { comment: reason.trim() } : {}),
      }),
    onSuccess: (_data, { kind }) => {
      setAction(null);
      setReason('');
      toast.show(kind === 'delivered' ? t.toastDelivered : t.toastDeliveryFailed, 'success');
      void client.invalidateQueries({ queryKey: ['my-shipments'] });
    },
    onError: (cause: unknown) => {
      toast.show(cause instanceof ApiError ? cause.text(locale) : t.errorTitle, 'danger');
    },
  });

  const cashTiyin = parseSumInput(cash);
  const recordCash = useMutation({
    mutationFn: (id: string) => {
      if (cashTiyin === null) throw new Error('amount');
      return api.courierCash(id, { amount: cashTiyin });
    },
    onSuccess: () => {
      setAction(null);
      setCash('');
      toast.show(t.toastCashRecorded, 'success');
      void client.invalidateQueries({ queryKey: ['my-shipments'] });
    },
    onError: (cause: unknown) => {
      toast.show(cause instanceof ApiError ? cause.text(locale) : t.errorTitle, 'danger');
    },
  });

  const card = (row: MyShipmentResponse): ReactNode => {
    const { order, shipment } = row;
    // Olinadigan summa — serverdan: buyurtma summasi − tasdiqlangan − kutilayotgan yozuvlar.
    const due = order.amountDue !== null && BigInt(order.amountDue) > 0n ? order.amountDue : null;
    const payment = due !== null ? t.deliveryCashDue(formatMoney(due, locale)) : t.deliveryPayPaid;
    const open = action?.id === shipment.id ? action.kind : null;
    const place = [order.regionNameUz, order.addressLine].filter(Boolean).join(', ');

    return (
      <li className="adm-delivery" key={shipment.id}>
        <header className="adm-delivery__head">
          <strong>
            {t.deliveryOrder} #{order.number}
          </strong>
          {scope === 'done' && (
            <span className="adm-badge">
              {locale === 'RU'
                ? (shipment.statusLabelRu ?? shipment.statusLabelUz)
                : shipment.statusLabelUz}
            </span>
          )}
        </header>

        <dl className="adm-delivery__facts">
          <dt>{t.deliveryCustomer}</dt>
          <dd>
            {order.customerName}
            {order.customerPhone !== null && (
              <>
                {' · '}
                <a href={`tel:${order.customerPhone}`} className="adm-delivery__phone">
                  {order.customerPhone}
                </a>
              </>
            )}
          </dd>
          <dt>{t.deliveryAddress}</dt>
          <dd>{place === '' ? '—' : place}</dd>
          {order.operatorNote !== null && (
            <>
              <dt>{t.deliveryNote}</dt>
              <dd>{order.operatorNote}</dd>
            </>
          )}
          <dt>{t.deliveryPayment}</dt>
          <dd>{payment}</dd>
        </dl>

        <h3 className="adm-panel__subtitle">{t.deliveryItems}</h3>
        <ul className="adm-items">
          {shipment.items.map((item) => (
            <li className="adm-items__row" key={item.id}>
              <span>{item.productName}</span>
              <span className="adm-items__qty">×{item.qty}</span>
            </li>
          ))}
        </ul>

        {scope === 'active' && open === null && (
          <div className="adm-delivery__actions">
            {due !== null && (
              <Button
                variant="secondary"
                onClick={() => {
                  setCash(formatSumInput(due));
                  setAction({ kind: 'cash', id: shipment.id });
                }}
              >
                {t.deliveryCashButton}
              </Button>
            )}
            <Button
              onClick={() => {
                setAction({ kind: 'delivered', id: shipment.id });
              }}
            >
              {t.deliveryDelivered}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setAction({ kind: 'failed', id: shipment.id });
              }}
            >
              {t.deliveryFailed}
            </Button>
          </div>
        )}

        {open === 'cash' && (
          <div className="adm-delivery__reason">
            <label className="aps-field__label" htmlFor={cashId}>
              {t.deliveryCashAmount}
            </label>
            <input
              id={cashId}
              className="aps-field__input"
              inputMode="decimal"
              value={cash}
              onChange={(event) => {
                setCash(event.target.value);
              }}
            />
            <span className="aps-field__hint">
              {cashTiyin === null ? t.deliveryCashInvalid : t.deliveryCashNoteWait}
            </span>
            <div className="adm-delivery__actions">
              <Button
                disabled={cashTiyin === null || BigInt(cashTiyin) <= 0n || recordCash.isPending}
                onClick={() => {
                  recordCash.mutate(shipment.id);
                }}
              >
                {t.deliveryCashSave}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setAction(null);
                }}
              >
                {t.deliveryCancel}
              </Button>
            </div>
          </div>
        )}

        {open === 'delivered' && (
          <div className="adm-delivery__actions">
            <Button
              disabled={finish.isPending}
              onClick={() => {
                finish.mutate({ id: shipment.id, kind: 'delivered' });
              }}
            >
              {t.deliveryConfirmDelivered}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAction(null);
              }}
            >
              {t.deliveryCancel}
            </Button>
          </div>
        )}

        {open === 'failed' && (
          <div className="adm-delivery__reason">
            <label className="aps-field__label" htmlFor={reasonId}>
              {t.deliveryReasonLabel}
            </label>
            <textarea
              id={reasonId}
              className="aps-field__input"
              rows={2}
              maxLength={500}
              placeholder={t.deliveryReasonPlaceholder}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <div className="adm-delivery__actions">
              <Button
                disabled={reason.trim() === '' || finish.isPending}
                onClick={() => {
                  finish.mutate({ id: shipment.id, kind: 'failed' });
                }}
              >
                {t.deliveryConfirmFailed}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setAction(null);
                  setReason('');
                }}
              >
                {t.deliveryCancel}
              </Button>
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <section className="adm-page">
      <h1 className="adm-page__title">{t.deliveriesTitle}</h1>

      <div className="aps-scroll-x" role="group" aria-label={t.deliveriesTitle}>
        <FilterChip
          label={t.deliveriesActive}
          active={scope === 'active'}
          onToggle={() => {
            setScope('active');
            setAction(null);
          }}
        />
        <FilterChip
          label={t.deliveriesDone}
          active={scope === 'done'}
          onToggle={() => {
            setScope('done');
            setAction(null);
          }}
        />
      </div>

      {list.isPending ? (
        <Skeleton height="160px" label={t.loading} />
      ) : list.isError ? (
        <div role="alert">
          <EmptyState
            title={list.error instanceof ApiError ? list.error.text(locale) : t.errorTitle}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  void list.refetch();
                }}
              >
                {t.retry}
              </Button>
            }
          />
        </div>
      ) : list.data.length === 0 ? (
        <EmptyState title={scope === 'active' ? t.deliveriesEmptyActive : t.deliveriesEmptyDone} />
      ) : (
        <ul className="adm-deliveries">{list.data.map(card)}</ul>
      )}
    </section>
  );
}
